import React, { useState, useEffect } from 'react';

import { PROYECTOS_COLLECTION} from '../utils/constants'
import { 
    collection, query, where, getDocs, Timestamp,
    orderBy} from 'firebase/firestore';


import 'react-toastify/dist/ReactToastify.css';
import { db } from '../firebase/config'

export const AntecedentesDashboard = () => {
    const [projects, setProjects] = useState([]);
    const [invoicesMap, setInvoicesMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [npuFilter, setNpuFilter] = useState('');
    const [serviceFilter, setServiceFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [providerFilter, setProviderFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const currentYear = new Date().getFullYear();
                const startYear = currentYear - 2;
                const startDate = new Date(startYear, 0, 1);
                const timestampStart = Timestamp.fromDate(startDate);

                const qProjects = query(
                    collection(db, PROYECTOS_COLLECTION),
                    where("fechaApertura", ">=", timestampStart),
                    orderBy("fechaApertura", "desc")
                );

                const qInvoices = query(collection(db, "facturas"));

                const [projectsSnap, invoicesSnap] = await Promise.all([
                    getDocs(qProjects),
                    getDocs(qInvoices)
                ]);

                const projectsData = projectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                const invMap = {};
                invoicesSnap.forEach(doc => {
                    invMap[doc.id] = doc.data().folio;
                });

                setProjects(projectsData);
                setInvoicesMap(invMap);

            } catch (err) {
                console.error("Error cargando antecedentes:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const uniqueServices = React.useMemo(() => {
        const services = projects.map(p => p.servicioNombre).filter(Boolean);
        return [...new Set(services)].sort();
    }, [projects]);

    const uniqueClients = React.useMemo(() => {
        const clients = projects.map(p => p.clienteNombre).filter(Boolean);
        return [...new Set(clients)].sort();
    }, [projects]);

    const uniqueProviders = React.useMemo(() => {
        const providers = projects.map(p => p.proveedorNombre).filter(Boolean);
        return [...new Set(providers)].sort();
    }, [projects]);

    const filteredProjects = projects.filter(p => {
        const matchNPU = !npuFilter || 
            (p.npu && p.npu.toLowerCase().includes(npuFilter.toLowerCase()));
            
        const matchClient = !clientFilter || p.clienteNombre === clientFilter;
        const matchProvider = !providerFilter || p.proveedorNombre === providerFilter;
        const matchService = !serviceFilter || p.servicioNombre === serviceFilter;

        return matchNPU && matchClient && matchProvider && matchService;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredProjects.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
                Historial de Proyectos ({new Date().getFullYear() - 2} - {new Date().getFullYear()})
            </h1>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg border flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs font-bold text-gray-500 mb-1">Buscar NPU</label>
                    <input
                        type="text"
                        placeholder="Escribe el NPU..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={npuFilter}
                        onChange={(e) => { setNpuFilter(e.target.value); setCurrentPage(1); }}
                    />
                </div>
                
                <div className="w-full md:w-auto">
                    <label className="block text-xs font-bold text-gray-500 mb-1">Filtrar por Cliente</label>
                    <select 
                        className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={clientFilter}
                        onChange={(e) => { setClientFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">Todos los Clientes</option>
                        {uniqueClients.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div className="w-full md:w-auto">
                    <label className="block text-xs font-bold text-gray-500 mb-1">Filtrar por Servicio</label>
                    <select 
                        className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={serviceFilter}
                        onChange={(e) => { setServiceFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">Todos los Servicios</option>
                        {uniqueServices.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                <div className="w-full md:w-auto">
                    <label className="block text-xs font-bold text-gray-500 mb-1">Filtrar por Proveedor</label>
                    <select 
                        className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        value={providerFilter}
                        onChange={(e) => { setProviderFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">Todos los Proveedores</option>
                        {uniqueProviders.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            </div>

            {loading ? <p>Cargando archivo histórico...</p> : (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50 text-gray-500 mb-1">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Cliente</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">NPU</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Servicio</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Proveedor</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Doc. Prov.</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Costo Prov.</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Notas Internas</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">PO Cliente</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Precio Cot.</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Factura Cliente</th>
                                <th className="px-4 py-3 text-left font-medium uppercase tracking-wider">Heyzine</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {currentItems.map(p => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium">{p.clienteNombre}</td>
                                    <td className="px-4 py-2font-medium">{p.npu}</td>
                                    <td className="px-4 py-2font-medium">{p.servicioNombre}</td>
                                    <td className="px-4 py-2">{p.proveedorNombre}</td>
                                    <td className="px-4 py-2 text-xs">
                                        <div>Cot: {p.cotizacionProveedorRef || '-'}</div>
                                        <div>PO: {p.poProveedor || '-'}</div>
                                    </td>
                                    <td className="px-4 py-2 text-red-600 font-medium">${(p.costoProveedor || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2">
                                        <div className="flex flex-col text-xs">
                                            <span>NE1: {p.fase1_numeroNotaInterna || '-'}</span>
                                            {p.fase2_numeroNotaInterna && <span>NE2: {p.fase2_numeroNotaInterna}</span>}
                                        </div>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-blue-600">
                                        <div>{p.poClienteRef || '-'}</div>
                                    </td>
                                    <td className="px-4 py-2 text-green-600 font-medium">${(p.precioCotizacionCliente || 0).toFixed(2)}</td>
                                    <td className="px-4 py-2 text-xs">
                                        {p.facturasClienteIds && p.facturasClienteIds.length > 0 
                                            ? p.facturasClienteIds.map(id => invoicesMap[id] || id).join(', ') 
                                            : '---'}
                                    </td>
                                    <td className="px-4 py-2">
                                        {p.urlHeyzine ? (
                                            <a href={p.urlHeyzine} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-bold">Ver</a>
                                        ) : <span className="text-gray-400">---</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-4 flex justify-between items-center">
                <span className="text-sm text-gray-700">Página {currentPage} de {totalPages || 1}</span>
                <div>
                    <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md bg-white mr-2 disabled:opacity-50">Anterior</button>
                    <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 border rounded-md bg-white disabled:opacity-50">Siguiente</button>
                </div>
            </div>
        </div>
    );
};